import { formatReportBodyDisplay, reportHeadingTone } from '../utils/reportBodyDisplay';
import React, { useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import { formatReportFieldLabelsHtml } from '../utils/reportFieldFormatting';
import { ChevronDown } from 'lucide-react';
import { resolveNextStepsSections, logNextStepsDiagnostic } from '../utils/nextStepsSections';
import { getPhase5SectionId, readPhase5ReportUi, writeExpandedSections } from '../utils/phase5ReportUi';
import { formatReportSentenceHtml } from '../utils/reportSentenceFormatting';
import { formatPhase5BodyStructure } from '../utils/phase5BodyStructure';
import { getReportHeadingPresentation, Phase5SectionHeading, phase5Tone } from './Phase5ReportSystem';

/**
 * RichReportRenderer
 * Renders AI-generated report content that contains a mix of Markdown
 * (bold, italics, lists, tables) and raw HTML (specifically <details>/<summary>
 * expandable blocks). Uses `marked` to convert the string to HTML, then renders
 * top-level accordion wrappers as controlled React elements while retaining each
 * summary/body's original formatted HTML.
 *
 * Only trusted, server-generated content should be passed here. This is used
 * for the "Why" (Patterns & Contributing Themes) and Next Steps reports which
 * come from our own LLM pipeline (not user input).
 */

// Configure marked once
marked.setOptions({
  gfm: true,
  breaks: false,
});

const stripCodeFenceWrapper = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  let t = raw.trim();
  // Fully wrapped in ``` ... ```
  const fullFence = t.match(/^```(?:html|markdown|md)?\s*\n([\s\S]*?)\n```\s*$/i);
  if (fullFence) return fullFence[1];
  // Only leading fence (LLM forgot to close). Strip it.
  t = t.replace(/^```(?:html|markdown|md)?\s*\n/i, '');
  // Only trailing fence
  t = t.replace(/\n?```\s*$/i, '');
  return t;
};

const WHY_HISTORICAL_SECTION_CONTRACT = Object.freeze([
    ['why_01', 'EXECUTIVE SUMMARY'],
    ['why_02', 'CORE RESILIENCE FACTORS AND BUFFER ANALYSIS'],
    ['why_03', 'DYNAMIC PATTERNS AND PARADOXES'],
    ['why_04', 'FAMILY SYSTEM AND TRIANGULATION MAPPING'],
    ['why_05', 'WHAT IS MISSING: THE ABSENCES'],
    ['why_06', 'THE ELEPHANT IN THE ROOM'],
    ['why_07', 'THERAPIST BLIND SPOT WARNING'],
    ['why_08', 'AREAS FOR CURIOSITY'],
    ['why_09', 'NARRATIVE INCONGRUENCES AND POTENTIAL FABRICATION MARKERS'],
    ['why_10', 'CLINICAL RELEVANCE NOTE: NOT A DIAGNOSIS'],
    ['why_11', 'MANDATORY DISCLAIMER'],
]);

export const WHY_SECTION_CONTRACT = Object.freeze([
    ['why_01', 'EXECUTIVE SUMMARY'],
    ['why_02', 'DATA AND VALENCE OVERVIEW'],
    ['why_03', 'CORE RESILIENCE FACTORS AND BUFFER ANALYSIS'],
    ['why_04', 'DYNAMIC PATTERNS, PARADOXES AND COMPETING HYPOTHESES'],
    ['why_05', 'INTERNAL INCONGRUENCES AND CONTRADICTIONS'],
    ['why_06', 'FAMILY, RELATIONAL AND CONTEXTUAL MAPPING'],
    ['why_07', 'WHAT IS MISSING'],
    ['why_08', 'THE ELEPHANT IN THE ROOM'],
    ['why_09', 'THERAPIST / ADMINISTRATOR BLIND-SPOT CAUTIONS'],
    ['why_10', 'PRIORITY AREAS FOR CURIOSITY'],
    ['why_11', 'NARRATIVE AND DATA INCONGRUENCES'],
    ['why_12', 'INTEGRATED FORMULATION'],
    ['why_13', 'CLINICAL RELEVANCE NOTE: NOT A DIAGNOSIS'],
    ['why_14', 'MANDATORY DISCLAIMER'],
]);

const REPORT_MAJOR_HEADINGS = Object.freeze({
  why: WHY_SECTION_CONTRACT.map(([, title]) => title),
  next_steps: [
    'NEXT STEPS EXECUTIVE SUMMARY', 'STRENGTHS LEVERAGE STRATEGY',
    'BEHAVIORAL MICRO-EXPERIMENTS', 'WHAT TO AVOID: EXPLORATION BOUNDARIES',
    'THERAPIST IMPLEMENTATION GUIDANCE', 'CLINICAL RELEVANCE NOTE: NOT A DIAGNOSIS',
    'MANDATORY DISCLAIMER', 'WHAT TO AVOID / BOUNDARIES',
    'CONVERSATIONAL / FOLLOW-UP GUIDANCE', 'CULTURAL / CONTEXTUAL CONSIDERATIONS',
  ],
});

const RESEARCH_REPORT_PREFIXES = Object.freeze({
  apa_case_parallels: 'apa',
  research_framework_synthesis: 'research_framework',
  developmental_ecological_cultural: 'dec',
});
const isResearchReport = (reportKey) => Object.prototype.hasOwnProperty.call(RESEARCH_REPORT_PREFIXES, reportKey);
const isCustomReport = (reportKey) => /^custom_report(?:_|$)/.test(String(reportKey || ''));

const cleanSourceHeading = (value) => String(value || '')
  .replace(/^\s*(?:#{1,6}\s*)?(?:[*_`]+\s*)?(?:\d+(?:\.\d+)*[.)]?\s*)?/, '')
  .replace(/(?:\s*[*_`]+)?\s*:?\s*$/, '').replace(/\s+/g, ' ').trim();

const normalizeWhyTitle = (value) => cleanSourceHeading(value).normalize('NFKC')
  .replace(/["'‘’“”`]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().toUpperCase();
const WHY_TITLE_SLOT = new Map(WHY_SECTION_CONTRACT.map(([, title], index) => [normalizeWhyTitle(title), index]));
const WHY_LEGACY_TITLE_SLOT = new Map([
  ['CORE RESILIENCE FACTORS AND BUFFER ASSESSMENT', 1],
]);

export const canonicalizeWhySections = (sections) => {
  if (!Array.isArray(sections)) return [];
  const unified = sections.some(section => WHY_TITLE_SLOT.has(normalizeWhyTitle(section.title))
    && !WHY_HISTORICAL_SECTION_CONTRACT.some(([, title]) => normalizeWhyTitle(title) === normalizeWhyTitle(section.title)));
  const contract = unified ? WHY_SECTION_CONTRACT : WHY_HISTORICAL_SECTION_CONTRACT;
  const titleSlots = new Map(contract.map(([, title], index) => [normalizeWhyTitle(title), index]));
  const usedSlots = new Set();
  let structuralMismatch = false;
  const canonical = sections.map((section, index) => {
    const sourceTitle = normalizeWhyTitle(section.title);
    const titleSlot = titleSlots.has(sourceTitle)
      ? titleSlots.get(sourceTitle)
      : WHY_LEGACY_TITLE_SLOT.get(sourceTitle);
    const sourceNumber = String(section.title || '').match(/^\s*(\d+)[.)]?\s+/);
    const numberedSlot = sourceNumber ? Number(sourceNumber[1]) - 1 : null;
    if (titleSlot !== undefined && numberedSlot !== null && titleSlot !== numberedSlot) structuralMismatch = true;
    // Only canonical saved IDs carry identity; old presentation slugs do not.
    const savedId = section.section_id ?? section.id;
    const savedSlot = contract.findIndex(([id]) => id === savedId);
    if (savedSlot >= 0 && ((titleSlot !== undefined && savedSlot !== titleSlot)
      || (numberedSlot !== null && savedSlot !== numberedSlot))) structuralMismatch = true;
    if (titleSlot === undefined && savedSlot < 0) structuralMismatch = true;
    const slot = savedSlot >= 0 ? savedSlot : titleSlot ?? numberedSlot ?? index;
    if (!contract[slot] || usedSlots.has(slot)) structuralMismatch = true;
    usedSlots.add(slot);
    const [id, title] = contract[slot] || [`why_invalid_${index + 1}`, sourceTitle];
    return { ...section, id, title, summaryHtml: title };
  });
  if (structuralMismatch) {
    return [{
      id: 'why-structure-warning',
      title: 'WHY Analysis STRUCTURE WARNING',
      summaryHtml: 'WHY Analysis STRUCTURE WARNING',
      bodyHtml: '<p>This saved Why Analysis has conflicting section identities and cannot be safely relabelled. Its clinical content has not been changed.</p>',
      defaultOpen: true,
    }];
  }
  return canonical;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderStructuredBody = (source, preserveSourceText = false) => {
  const formatted = formatReportFieldLabelsHtml(preserveSourceText ? String(source || '') : marked.parse(String(source || '')));
  const blockHtml = /<(?:p|div|ul|ol|table|h[1-6]|blockquote|pre)\b/i.test(formatted) ? formatted : `<p>${formatted}</p>`;
  return preserveSourceText ? formatted : formatReportSentenceHtml(formatPhase5BodyStructure(blockHtml));

};

// Providers use several block-level subsection shapes.  The marker must occupy
// its complete source block (a whole Markdown/strong line or a semantic h3-h6)
// before it is promoted.  This is deliberately structural: a bold label at
// the start of a numbered evidence sentence is left in the subsection body.
const INTERNAL_LABELS = new Set([
  'EVIDENCE', 'HYPOTHESIS', 'DEEP HYPOTHESIS', 'CLINICAL RELEVANCE',
  'CULTURAL AND CONTEXTUAL LENS', 'DISTRESS-LANGUAGE LINK',
  'DISORDER-RELEVANT STRUCTURAL NOTE', 'SESSION-READY QUESTIONS',
  'COST OR BUFFERS', 'MAINTENANCE CYCLE', 'TRIGGER', 'AMPLIFIER',
  'BUFFER', 'POINT CITATIONS', 'OBSERVED EVIDENCE', 'CONTRADICTORY OR LIMITING EVIDENCE',
  'COMPETING HYPOTHESES', 'EVIDENCE SUPPORT LEVEL', 'VERIFICATION QUESTION',
]);
const cleanInternalTitle = (value) => String(value || '')
  .replace(/<[^>]*>/g, '').replace(/\*{1,3}|_{1,3}|`/g, '')
  .replace(/^\s*(?:#{1,6}\s*)?/, '').replace(/\s+/g, ' ').trim().replace(/:$/, '').trim();
const isInternalSubsectionTitle = (value) => {
  const title = cleanInternalTitle(value);
  const upper = title.toLocaleUpperCase();
  const letters = title.replace(/[^\p{L}]/gu, '');
  if (letters.length < 4 || INTERNAL_LABELS.has(upper) || title.length > 140) return false;
  const labelRemainder = title.replace(/^(?:[A-Z]\.\s+|\d+(?:\.\d+)*[.)]?\s+)/, '').toLocaleUpperCase();
  if ([...INTERNAL_LABELS].some((label) => labelRemainder === label || labelRemainder.startsWith(`${label}:`))
      || /^(?:SUPPORTING EVIDENCE|LIMITING \/ CONTRADICTORY EVIDENCE|ALTERNATIVE SIMPLER OR CONTEXTUAL EXPLANATION|WHAT REMAINS UNKNOWN|VERIFICATION QUESTION)(?:\s*:|$)/.test(labelRemainder)) return false;
  const numberedMatch = title.match(/^(\d+(?:\.\d+)*[.)]?)\s+(.+)$/);
  const numbered = Boolean(numberedMatch && numberedMatch[2] === numberedMatch[2].toLocaleUpperCase());
  const lettered = /^[A-Z]\.[ \t]+/.test(title);
  const named = /^(?:PATTERN|EXPLORATION|ANOMALY|INCONGRUENCE|INTERPRETATION|AREA|ACTIVATION|FRAMING STRATEGY|THE )\b/i.test(title);
  const allCaps = title === upper && !/[.!?]$/.test(title);
  return numbered || lettered || named || (allCaps && title.split(/\s+/).length >= 2);
};

const parseInternalMarker = (line) => {
  const trimmed = String(line || '').trim();
  let match = trimmed.match(/^#{2,6}\s+(.+?)\s*$/);
  if (match && isInternalSubsectionTitle(match[1])) return { title: cleanInternalTitle(match[1]) };
  match = trimmed.match(/^<h([3-6])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>\s*$/i);
  if (match && isInternalSubsectionTitle(match[2])) return { title: cleanInternalTitle(match[2]) };
  match = trimmed.match(/^\*\*([\s\S]+?)\*\*\s*:?[ \t]*$/);
  if (match && isInternalSubsectionTitle(match[1])) return { title: cleanInternalTitle(match[1]) };
  match = trimmed.match(/^<strong(?:\s[^>]*)?>([\s\S]*?)<\/strong>\s*:?[ \t]*$/i);
  if (match && isInternalSubsectionTitle(match[1])) return { title: cleanInternalTitle(match[1]) };
  match = trimmed.match(/^<(?:p|div|section)\b[^>]*>\s*(?:<strong|<b)\b[^>]*>([\s\S]*?)<\/(?:strong|b)>\s*<\/(?:p|div|section)>$/i);
  if (match && isInternalSubsectionTitle(match[1])) return { title: cleanInternalTitle(match[1]) };
  match = trimmed.match(/^(?:\d+(?:\.\d+)*[.)]?|[A-Z][.)])\s+(.+?)\s*$/);
  if (match && isInternalSubsectionTitle(trimmed)
      && (!trimmed.includes('**') || /^[A-Z0-9][A-Z0-9 .:&/–—()'\-]+$/.test(cleanInternalTitle(trimmed)))) {
    return { title: cleanInternalTitle(trimmed) };
  }
  if (isInternalSubsectionTitle(trimmed) && /^[A-Z][A-Z0-9 &/,:–—()'\-]+$/.test(trimmed)) {
    return { title: cleanInternalTitle(trimmed) };
  }
  return null;
};

export const splitInternalSubsections = (source, parentId, preserveSourceText = false) => {
  const renderMaybeEmpty = (value) => String(value || '').trim()
    ? renderStructuredBody(value, preserveSourceText) : '';
  if (typeof source !== 'string' || !source.trim()) return { bodyHtml: renderMaybeEmpty(source), internalSections: [] };
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const markers = lines.map((line, index) => {
    const marker = parseInternalMarker(line);
    // Preserve a source colon in the display title without changing the
    // production heading recognizer or subsection boundaries.
    const sourceTitle = line.replace(/<[^>]*>/g, '').replace(/[*_`]/g, '').trim();
    return marker ? { index, title: marker.title + (sourceTitle.endsWith(':') ? ':' : '') } : null;
  }).filter(Boolean);
  if (!markers.length) return { bodyHtml: renderMaybeEmpty(source), internalSections: [] };
  const internalSections = markers.map((marker, index) => {
    const end = markers[index + 1]?.index ?? lines.length;
    return {
      id: `${parentId}-internal-${index + 1}`,
      title: marker.title,
      bodyHtml: renderMaybeEmpty(lines.slice(marker.index + 1, end).join('\n').trim()),
    };
  });
  return {
    bodyHtml: renderMaybeEmpty(lines.slice(0, markers[0].index).join('\n').trim()),
    internalSections,
  };
};

const attachInternalSubsections = (section, preserveSourceText = false) => {
  if (section.internalSections?.length || !section.bodyHtml) return section;
  const split = splitInternalSubsections(String(section.bodyHtml), section.id, preserveSourceText);
  return split.internalSections.length ? { ...section, bodyHtml: split.bodyHtml, internalSections: split.internalSections } : section;
};

// Current providers sometimes return the entire report as one plain-text line:
// "EXECUTIVE SUMMARY body ... CORE ... body". Parse the original transport text
// before Markdown can turn that shape into one undifferentiated <p> node.
export const parseKnownPlainTextSections = (source, reportKey, preserveSourceText = false) => {
  const headings = REPORT_MAJOR_HEADINGS[reportKey];
  if (!headings || typeof source !== 'string' || !source.trim()) return [];
  const cleaned = stripCodeFenceWrapper(source).replace(/\r\n?/g, '\n');
  const recognizedHeadings = reportKey === 'why'
    ? [...new Set([...headings, ...WHY_HISTORICAL_SECTION_CONTRACT.map(([, title]) => title), ...WHY_LEGACY_TITLE_SLOT.keys()])]
    : headings;
  const alternatives = [...recognizedHeadings]
    .sort((a, b) => b.length - a.length)
    .map((title) => escapeRegExp(title).replace(/\\ /g, '\\s+'))
    .join('|');
  const matcher = new RegExp(`(?:^|\\s)(?:#{1,6}\\s*)?(?:[*_\`]\\s*)*(?:\\d+(?:\\.\\d+)*[.)]?\\s*)?(${alternatives})(?:\\s*[*_\`]+)?(?=\\s|:|$)`, 'giu');
  let allMatches = [...cleaned.matchAll(matcher)];
  if (reportKey === 'why') {
    const knownTitles = new Set(recognizedHeadings.map(normalizeWhyTitle));
    // A heading occupies its whole line. A numbered prose item such as
    // "4. The Elephant in the Room: explanation ..." is still report content.
    // Prefer explicit lines to the legacy single-line transport scanner.
    const lineMarkers = [...cleaned.matchAll(/[^\n]+/g)].filter((match) =>
      knownTitles.has(normalizeWhyTitle(match[0]))
      || /^\s*\d+[.)]?\s+[A-Z][A-Z /:–—-]{4,}\s*$/.test(match[0])
    ).map((match) => Object.assign([match[0], match[0].trim()], { index: match.index }));
    if (lineMarkers.filter((match) => knownTitles.has(normalizeWhyTitle(match[1]))).length >= 2) {
      allMatches = lineMarkers;
    }
  }
  const isNumberedLineMarker = (match) => {
    const titleOffset = match[0].indexOf(match[1]);
    const titleIndex = match.index + Math.max(titleOffset, 0);
    const lineStart = cleaned.lastIndexOf('\n', titleIndex - 1) + 1;
    return /^\s*(?:#{1,6}\s*)?(?:[*_`]\s*)*\d+(?:\.\d+)*[.)]?\s*$/.test(
      cleaned.slice(lineStart, titleIndex)
    );
  };
  const numberedWhyMarkers = reportKey === 'why' ? allMatches.filter(isNumberedLineMarker) : [];
  // A complete numbered historical report has an unambiguous structural
  // grammar. In that shape, canonical title phrases inside prose are content,
  // not headings (for example "The Elephant in the Room" in an executive body).
  const rawMatches = reportKey === 'why' && numberedWhyMarkers.length >= WHY_HISTORICAL_SECTION_CONTRACT.length
    ? numberedWhyMarkers
    : allMatches;
  // A structured provider may repeat its own heading as the first line of the
  // content field. The backend's display serializer also supplies that heading.
  // Collapse only adjacent, canonically identical markers with no body between
  // them. Distinct blocks resolving to one ID remain present and are rejected by
  // canonicalizeWhySections as a structural conflict.
  const matches = reportKey === 'why' ? rawMatches.filter((match, index) => {
    const previous = rawMatches[index - 1];
    if (!previous) return true;
    const between = cleaned.slice(previous.index + previous[0].length, match.index)
      .replace(/^\s*:\s*/, '').trim();
    return between.length > 0 || normalizeWhyTitle(previous[1]) !== normalizeWhyTitle(match[1]);
  }) : rawMatches;
  if (!matches.length) return [];
  const counts = {};
  return matches.map((match, index) => {
    const title = reportKey === 'why' ? match[1].trim() : cleanSourceHeading(match[1]);
    const bodyStart = match.index + match[0].length;
    const bodySource = cleaned.slice(bodyStart, matches[index + 1]?.index ?? cleaned.length)
      .replace(/^\s*:\s*/, '').trim();
    const baseId = getPhase5SectionId(title);
    const id = getPhase5SectionId(title, counts[baseId] || 0);
    counts[baseId] = (counts[baseId] || 0) + 1;
    const rendered = splitInternalSubsections(bodySource, baseId, preserveSourceText);
    return {
      id, title, summaryHtml: title,
      bodyHtml: rendered.bodyHtml,
      internalSections: rendered.internalSections,
      defaultOpen: index === 0,
    };
  });
};

// Research reports intentionally have report-specific headings. Parse their
// original Markdown/plain transport instead of applying the Why contract.
export const parseResearchSections = (source, reportKey) => {
  const prefix = RESEARCH_REPORT_PREFIXES[reportKey];
  if (!prefix || typeof source !== 'string' || !source.trim()) return [];
  const lines = stripCodeFenceWrapper(source).replace(/\r\n?/g, '\n').split('\n');
  const headingPattern = /^\s*(?:#{1,6}\s+|(?:\d+[.)]|[A-H][.)])\s+)(.+?)\s*$/;
  const boldHeadingPattern = /^\s*\*\*(?:[A-H][.)]\s+)?([^*]{5,})\*\*\s*:?\s*$/;
  const headings = [];
  lines.forEach((line, index) => {
    const match = line.match(headingPattern) || line.match(boldHeadingPattern);
    if (!match) return;
    const title = cleanSourceHeading(match[1]);
    // Avoid promoting ordinary sentence-like lines or short labels to cards.
    const isBoldHeading = line.trim().startsWith('**') && line.trim().endsWith('**');
    if (title.length < 5 || (!line.includes('#') && !isBoldHeading && !/^[A-H]\.|^\d+[.)]/.test(title) && !/^[A-Z][A-Z0-9 /&’'–—:()]{4,}$/.test(title))) return;
    headings.push({ index, title });
  });
  if (!headings.length) return [];
  return headings.map((heading, index) => {
    const bodySource = lines.slice(heading.index + 1, headings[index + 1]?.index ?? lines.length).join('\n').trim();
    return {
      id: `${prefix}_${String(index + 1).padStart(2, '0')}`,
      title: heading.title,
      summaryHtml: heading.title,
      bodyHtml: renderStructuredBody(bodySource),
      defaultOpen: index === 0,
    };
  }).filter((section) => section.title.trim() && section.bodyHtml.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim());
};

// Research providers sometimes emit a heading-less <details> fragment (most
// often an empty <summary> or a numeric-only marker such as "1."). The shared
// heading presenter removes that marker, which previously left a blank title.
// Keep valid source wording and fold only heading-less content into the prior
// valid section; a leading orphan is not promoted to an accordion.
export const normalizeResearchSections = (sections, reportKey) => {
  if (!isResearchReport(reportKey) || !Array.isArray(sections)) return sections;
  const prefix = RESEARCH_REPORT_PREFIXES[reportKey];
  const normalized = [];
  sections.forEach((section) => {
    const sourceHeading = String(section?.title || '').replace(/\s+/g, ' ').trim();
    const renderedTitle = /^\d+(?:\.\d+)*[.)]?$/.test(sourceHeading)
      ? ''
      : getReportHeadingPresentation(sourceHeading, '').title.trim();
    const bodyHtml = String(section?.bodyHtml || '').trim();
    const bodyText = bodyHtml.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
    if (!renderedTitle) {
      if (bodyText && normalized.length) {
        normalized[normalized.length - 1].bodyHtml += bodyHtml;
        normalized[normalized.length - 1].section_content += bodyHtml;
      }
      return;
    }
    if (!bodyText) return;
    const sectionId = `${prefix}_${String(normalized.length + 1).padStart(2, '0')}`;
    normalized.push({
      ...section,
      id: sectionId,
      title: sourceHeading,
      summaryHtml: sourceHeading,
      bodyHtml,
      section_id: sectionId,
      section_title: sourceHeading,
      section_content: bodyHtml,
      defaultOpen: normalized.length === 0,
    });
  });
  return normalized;
};

const htmlText = (value) => String(value || '').replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
const validCustomTitle = (value) => {
  const title = cleanSourceHeading(value);
  return title.length > 0
    && !/^\d+(?:\.\d+)*[.)]?$/.test(title)
    && /[\p{L}]/u.test(title)
    && !/^[\p{P}\p{S}\s]+$/u.test(title);
};
const isCustomMetadataHeading = (value) => /^CLIENT\s*:/i.test(String(value || '').trim());

// Custom Reports own their headings, so this parser detects structure rather
// than matching application-defined section names. Markdown has already been
// converted to HTML, which also makes native HTML headings follow one path.
export const parseCustomReportSections = (html, source = null) => {
  if (!html || typeof DOMParser === 'undefined') return [];
  const documentNode = new DOMParser().parseFromString(String(html), 'text/html');
  const nodes = [...documentNode.body.children];
  const explicitHeadings = nodes.map((node, index) => ({ node, index }))
    .filter(({ node }) => /^H[1-3]$/.test(node.tagName)
      && validCustomTitle(node.textContent) && !isCustomMetadataHeading(node.textContent));
  const levelCounts = explicitHeadings.reduce((counts, { node }) => {
    counts[node.tagName] = (counts[node.tagName] || 0) + 1; return counts;
  }, {});
  // Prefer the most specific repeated heading level. This excludes a report
  // title while retaining its repeated major children (the live provider emits
  // one H1 title followed by numbered H2 sections).
  const repeatedLevel = ['H2', 'H1', 'H3'].find((tag) => levelCounts[tag] >= 2);
  let headings = repeatedLevel ? explicitHeadings.filter(({ node }) => node.tagName === repeatedLevel) : explicitHeadings;
  if (headings.length < 2) {
    headings = nodes.map((node, index) => {
      if (node.tagName !== 'P') return null;
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      const numbered = /^\d+(?:\.\d+)*[.)]\s+\S/.test(text);
      const uppercase = text.length >= 5 && text.length <= 180
        && text === text.toUpperCase() && /[\p{L}]/u.test(text);
      return (numbered || uppercase) && validCustomTitle(text) ? { node, index } : null;
    }).filter(Boolean);
  }
  if (headings.length < 2 && typeof source === 'string') {
    const lines = stripCodeFenceWrapper(source).replace(/\r\n?/g, '\n').split('\n');
    const rawHeadings = lines.map((line, index) => {
      const match = line.match(/^\s*(\d+(?:\.\d+)*[.)])\s+(.+?)\s*$/);
      // Requiring blank separation prevents ordinary consecutive numbered list
      // items from being promoted to major sections.
      if (!match || (index > 0 && lines[index - 1].trim()) || (lines[index + 1] || '').trim()) return null;
      return validCustomTitle(match[2]) ? { index, title: match[2] } : null;
    }).filter(Boolean);
    if (rawHeadings.length >= 2) {
      const normalized = rawHeadings.map((heading, index) => {
        const bodySource = lines.slice(heading.index + 1, rawHeadings[index + 1]?.index ?? lines.length).join('\n').trim();
        const sectionId = `custom_${String(index + 1).padStart(2, '0')}`;
        return { id: sectionId, title: cleanSourceHeading(heading.title), summaryHtml: heading.title,
          bodyHtml: renderStructuredBody(bodySource), section_id: sectionId,
          section_title: cleanSourceHeading(heading.title), section_content: renderStructuredBody(bodySource),
          defaultOpen: index === 0 };
      }).filter((section) => section.section_title.trim() && htmlText(section.section_content));
      if (normalized.length >= 2) return normalized;
    }
  }
  if (headings.length < 2) return [];
  const normalized = [];
  headings.forEach((heading, index) => {
    const title = cleanSourceHeading(heading.node.textContent);
    const end = headings[index + 1]?.index ?? nodes.length;
    const bodyNodes = normalized.length === 0
      ? [...nodes.slice(0, heading.index), ...nodes.slice(heading.index + 1, end)]
      : nodes.slice(heading.index + 1, end);
    const bodyHtml = renderStructuredBody(bodyNodes.map((node) => node.outerHTML).join(''));
    if (!validCustomTitle(title) || !htmlText(bodyHtml)) return;
    const sectionId = `custom_${String(normalized.length + 1).padStart(2, '0')}`;
    normalized.push({
      id: sectionId, title, summaryHtml: title, bodyHtml,
      section_id: sectionId, section_title: title, section_content: bodyHtml,
      defaultOpen: normalized.length === 0,
    });
  });
  return normalized.length >= 2 ? normalized : [];
};

export const normalizeCustomSections = (sections, reportKey) => {
  if (!isCustomReport(reportKey) || !Array.isArray(sections)) return sections;
  const normalized = [];
  sections.forEach((section) => {
    const title = cleanSourceHeading(section?.title);
    const bodyHtml = String(section?.bodyHtml || '').trim();
    if (!validCustomTitle(title)) {
      if (htmlText(bodyHtml) && normalized.length) normalized[normalized.length - 1].bodyHtml += bodyHtml;
      return;
    }
    if (!htmlText(bodyHtml)) return;
    const sectionId = `custom_${String(normalized.length + 1).padStart(2, '0')}`;
    normalized.push({ ...section, id: sectionId, title, summaryHtml: title, bodyHtml,
      section_id: sectionId, section_title: title, section_content: bodyHtml,
      defaultOpen: normalized.length === 0 });
  });
  return normalized.length >= 2 ? normalized : [];
};

const parseLegacyReportAccordionSections = (html, reportKey = null, source = null, preserveSourceText = false) => {
  const structureBody = preserveSourceText ? (value) => value : formatPhase5BodyStructure;
  if (!html || typeof DOMParser === 'undefined') return [];
  const documentNode = new DOMParser().parseFromString(String(html), 'text/html');
  const detailNodes = [...documentNode.body.querySelectorAll('details')]
    .filter((node) => !node.parentElement?.closest('details'));
  if (!detailNodes.length) {
    if (isCustomReport(reportKey)) return parseCustomReportSections(html, source);
    const researchSections = parseResearchSections(source, reportKey);
    if (researchSections.length) return researchSections;
    const sourceSections = parseKnownPlainTextSections(source, reportKey, preserveSourceText);
    if (sourceSections.length) return sourceSections;
  }
  if (!detailNodes.length && reportKey === 'next_steps') {
    const internalLabels = new Set(['PRIMARY EXPLORATION DIRECTION', 'TOP PATTERNS ADDRESSED', 'DISTRESS THEME', 'PRIMARY STRENGTH LEVERAGED', 'PRIMARY CAUTION', 'ACTIVE STRENGTHS USED', 'DORMANT STRENGTHS INVITED', 'BUFFER CONSIDERATION', 'ACTIVATION PLAN', 'EXPLORATION TITLE', 'CONFIDENCE IN FEASIBILITY', 'JUSTIFICATION', 'INSIGHT ADDRESSED', 'CONTINGENCY PLAN', 'RATIONALE']);
    const majorHints = new Set(['NEXT STEPS EXECUTIVE SUMMARY', 'STRENGTHS LEVERAGE STRATEGY', 'BEHAVIORAL MICRO-EXPERIMENTS', 'WHAT TO AVOID: EXPLORATION BOUNDARIES', 'THERAPIST IMPLEMENTATION GUIDANCE', 'CLINICAL RELEVANCE NOTE: NOT A DIAGNOSIS', 'MANDATORY DISCLAIMER', 'WHAT TO AVOID / BOUNDARIES', 'CONVERSATIONAL / FOLLOW-UP GUIDANCE', 'CULTURAL / CONTEXTUAL CONSIDERATIONS']);
    if (typeof source === 'string' && source.trim() && !/<(?:html|body|p|div|h[1-6]|details)\b/i.test(source)) {
      const lines = source.replace(/\r\n?/g, '\n').split('\n');
      const normalize = (value) => value.replace(/^\s*\d+[.)]?\s*/, '').replace(/^\*+|\*+$/g, '').replace(/:$/, '').replace(/\s+/g, ' ').trim().toUpperCase();
      const starts = lines.map((line) => majorHints.has(normalize(line)) ? line : null).map((line, index) => line ? index : -1).filter((index) => index >= 0);
      if (starts.length) return starts.map((start, index) => {
        const end = starts[index + 1] ?? lines.length;
        const title = lines[start].trim();
        const bodySource = lines.slice(start + 1, end).join('\n').trim();
        return { id: getPhase5SectionId(title), title: title.replace(/^\d+[.)]?\s*/, '').replace(/:$/, '').trim(), summaryHtml: title.replace(/^\d+[.)]?\s*/, '').replace(/:$/, '').trim(), bodyHtml: structureBody(formatReportSentenceHtml(marked.parse(bodySource))), defaultOpen: index === 0 };
      });
    }
    const nodes = Array.from(documentNode.body.children);
    const headings = nodes.map((node) => {
      const tag = node.tagName.toLowerCase();
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      const label = text.replace(/^\d+[.)]?\s*/, '').replace(/:$/, '').trim().toUpperCase();
      const numberedUpper = /^\d+[.)]?\s+[A-Z][A-Z0-9 /&’\-]{4,}$/.test(text);
      return !internalLabels.has(label) && (/^h[1-3]$/.test(tag) || majorHints.has(label) || numberedUpper) ? { text } : null;
    });
    const indexes = headings.map((heading, index) => heading ? index : -1).filter((index) => index >= 0);
    if (!indexes.length) return [];
    return indexes.map((start, index) => {
      const end = indexes[index + 1] ?? nodes.length;
      const title = headings[start].text;
      return { id: getPhase5SectionId(title), title, summaryHtml: nodes[start].innerHTML || title,
        bodyHtml: structureBody(formatReportSentenceHtml(nodes.slice(start + 1, end).map((node) => node.outerHTML).join(''))), defaultOpen: index === 0 };
    });
  }
  const counts = {};
  const parsedDetails = detailNodes.map((detail, index) => {
    const summary = [...detail.children].find((child) => child.tagName.toLowerCase() === 'summary');
    const title = summary?.textContent?.replace(/\s+/g, ' ').trim() || ((isResearchReport(reportKey) || isCustomReport(reportKey)) ? '' : `section-${index + 1}`);
    const baseId = getPhase5SectionId(title);
    const id = getPhase5SectionId(title, counts[baseId] || 0);
    counts[baseId] = (counts[baseId] || 0) + 1;
    const body = detail.cloneNode(true);
    const bodySummary = [...body.children].find((child) => child.tagName.toLowerCase() === 'summary');
    bodySummary?.remove();
    return {
      id,
      ...(reportKey === 'why' && (detail.getAttribute('section_id') || detail.getAttribute('data-section-id') || detail.id)
        ? { section_id: detail.getAttribute('section_id') || detail.getAttribute('data-section-id') || detail.id } : {}),
      title,
      summaryHtml: summary?.innerHTML || title,
      bodyHtml: structureBody(formatReportSentenceHtml(body.innerHTML)),
      defaultOpen: detail.hasAttribute('open'),
    };
  });
  return normalizeCustomSections(normalizeResearchSections(parsedDetails, reportKey), reportKey);
};

export const parseReportAccordionSections = (html, reportKey = null, source = null, preserveSourceText = false) =>
  reportKey === 'next_steps'
    ? resolveNextStepsSections(source || html, () => parseLegacyReportAccordionSections(html, reportKey, source, preserveSourceText)).sections
    : parseLegacyReportAccordionSections(html, reportKey, source, preserveSourceText);

const RichReportRenderer = ({ sourceResponses = [], content, testId, sessionId, reportKey, reportVersion, generationRequestId, preserveSourceText = false, expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => {
  const screenPresentation = reportKey === 'why' || reportKey === 'next_steps';
  const formatBodyDisplay = (body) => screenPresentation ? formatReportBodyDisplay(body, sourceResponses) : body;
  const html = useMemo(() => {
    if (!content) return '';
    try {
      const cleaned = stripCodeFenceWrapper(content);
      return marked.parse(cleaned);
    } catch (e) {
      // Fall back to escaped plain text if parsing fails for any reason
      const div = document.createElement('div');
      div.textContent = content;
      return `<pre>${div.innerHTML}</pre>`;
    }
  }, [content]);
  const nextAnalysis = useMemo(() => reportKey === 'next_steps'
    ? resolveNextStepsSections(stripCodeFenceWrapper(content) || html, () => parseLegacyReportAccordionSections(html, reportKey, stripCodeFenceWrapper(content), preserveSourceText))
    : null, [html, content, reportKey, preserveSourceText]);
  useEffect(() => {
    if (!nextAnalysis || !content) return;
    void logNextStepsDiagnostic(content, nextAnalysis.diagnostic, { sessionId, reportVersion, generationRequestId }).catch(() => {});
  }, [nextAnalysis, content, sessionId, reportVersion, generationRequestId]);
  const sections = useMemo(() => {
    if (nextAnalysis) return nextAnalysis.sections.map((section) => attachInternalSubsections(section, preserveSourceText));
    const parsed = parseReportAccordionSections(html, reportKey, stripCodeFenceWrapper(content), preserveSourceText || reportKey === 'why');
    const normalized = reportKey === 'why' ? canonicalizeWhySections(parsed) : normalizeCustomSections(normalizeResearchSections(parsed, reportKey), reportKey);
    return normalized.map((section) => attachInternalSubsections(section, preserveSourceText || reportKey === 'why'));
  }, [html, reportKey, content, preserveSourceText, nextAnalysis]);
  const sectionIdKey = sections.map((section) => section.id).join('\u0000');
  const internalSectionIdKey = sections
    .flatMap((section) => (section.internalSections || []).map((subsection) => subsection.id))
    .join('\u0000');
  const initialExpanded = () => {
    const reportStates = readPhase5ReportUi(sessionId).expandedSections || {};
    return new Set(Object.prototype.hasOwnProperty.call(reportStates, reportKey)
      ? reportStates[reportKey]
      : sections.filter((section) => section.defaultOpen).map((section) => section.id));
  };
  const [expandedIds, setExpandedIds] = useState(initialExpanded);
  const [expandedInternalIds, setExpandedInternalIds] = useState(() => new Set());
  const controlled = Array.isArray(expandedSectionIds);
  const effectiveExpandedIds = controlled ? new Set(expandedSectionIds) : expandedIds;

  useEffect(() => {
    if (controlled) return;
    const reportStates = readPhase5ReportUi(sessionId).expandedSections || {};
    const storedExists = Object.prototype.hasOwnProperty.call(reportStates, reportKey);
    const available = new Set(sections.map((section) => section.id));
    setExpandedIds(() => {
      const source = storedExists
        ? new Set(reportStates[reportKey])
        : new Set(sections.filter((section) => section.defaultOpen).map((section) => section.id));
      const reconciled = new Set([...source].filter((id) => available.has(id)));
      if (storedExists) writeExpandedSections(sessionId, reportKey, [...reconciled]);
      return reconciled;
    });
  }, [sectionIdKey, reportKey, sessionId, controlled]);

  useEffect(() => { onSectionIdsChange?.(sections.map((section) => section.id)); }, [sectionIdKey, onSectionIdsChange]);

  // Nested state is local UI state. New content and remounts begin with every
  // detected subsection collapsed; it is never written to the report UI store.
  useEffect(() => {
    setExpandedInternalIds(new Set());
  }, [internalSectionIdKey]);

  const handleToggle = (sectionId, open) => {
    if (controlled) {
      const next = new Set(expandedSectionIds);
      if (open) next.add(sectionId); else next.delete(sectionId);
      onExpandedSectionIdsChange?.([...next]);
      return;
    }
    setExpandedIds((current) => {
      const next = new Set(current);
      if (open) next.add(sectionId); else next.delete(sectionId);
      writeExpandedSections(sessionId, reportKey, [...next]);
      return next;
    });
  };

  if (!content) return null;

  return (
    <div
      className="phase5-report rich-report-renderer text-gray-800"
      data-testid={testId}
    >
      {sections.length ? sections.map((section, sectionIndex) => {
        const heading = getReportHeadingPresentation(section.title, sectionIndex + 1);
        return (
        <details
          key={section.id}
          className={`phase5-report-section ${phase5Tone(sectionIndex)}`}
          open={effectiveExpandedIds.has(section.id)}
          data-phase5-section-id={section.id}
        >
          <summary onClick={(event) => {
            // Keep the native details element controlled by React. Preventing
            // the browser's implicit toggle avoids toggle-event races when
            // Expand All/Collapse All updates every section in one render.
            event.preventDefault();
            handleToggle(section.id, !effectiveExpandedIds.has(section.id));
          }}>
            <Phase5SectionHeading index={sectionIndex} displayNumber={heading.number}>
              <span>{heading.title}</span>
            </Phase5SectionHeading>
          </summary>
          <div className="phase5-section-body">
            {section.bodyHtml && <div dangerouslySetInnerHTML={{ __html: formatBodyDisplay(formatReportFieldLabelsHtml(section.bodyHtml)) }} />}
            {(section.internalSections || []).map((subsection) => {
              const expanded = expandedInternalIds.has(subsection.id);
              const controlId = `${subsection.id}-control`;
              const regionId = `${subsection.id}-content`;
              const toggle = () => setExpandedInternalIds((current) => {
                const next = new Set(current);
                if (next.has(subsection.id)) next.delete(subsection.id); else next.add(subsection.id);
                return next;
              });
              return <div className="phase5-internal-subsection" key={subsection.id}>
                <button
                  type="button"
                  id={controlId}
                  className="phase5-internal-subsection-button" data-report-tone={screenPresentation ? reportHeadingTone(subsection.title) : undefined}
                  data-testid={`internal-subsection-${subsection.id}`}
                  aria-expanded={expanded}
                  aria-controls={regionId}
                  onClick={(event) => { event.stopPropagation(); toggle(); }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle();
                    }
                  }}
                >
                  <ChevronDown className={`phase5-internal-subsection-chevron${expanded ? ' is-expanded' : ''}`} aria-hidden="true" />
                  <span>{subsection.title}</span>
                </button>
                <div
                  id={regionId}
                  role="region"
                  aria-labelledby={controlId}
                  className="phase5-internal-subsection-content"
                  hidden={!expanded}
                  dangerouslySetInnerHTML={{ __html: formatBodyDisplay(formatReportFieldLabelsHtml(subsection.bodyHtml)) }}
                />
              </div>;
            })}
          </div>
        </details>
      ); }) : <div className="phase5-section-body" dangerouslySetInnerHTML={{ __html: formatBodyDisplay(formatReportFieldLabelsHtml(preserveSourceText || reportKey === 'why' ? html : formatPhase5BodyStructure(formatReportSentenceHtml(html)))) }} />}
      <style>{`
        .rich-report-renderer details p,
        .rich-report-renderer details ul,
        .rich-report-renderer details ol {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .rich-report-renderer table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.75rem 0;
        }
        .rich-report-renderer th,
        .rich-report-renderer td {
          border: 1px solid #e5e7eb;
          padding: 0.5rem 0.75rem;
          text-align: left;
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
        }
        .rich-report-renderer th {
          background: #f3f4f6;
          font-weight: 600;
        }
        .rich-report-renderer .phase5-internal-subsection {
          margin: 0.75rem 0;
          border-left: 3px solid rgba(79, 70, 229, 0.2);
          padding-left: 0.75rem;
        }
        .rich-report-renderer .phase5-internal-subsection-button {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          width: 100%;
          padding: 0.35rem 0;
          color: inherit;
          background: transparent;
          border: 0;
          text-align: left;
          font: inherit;
          font-weight: 700;
          cursor: pointer;
        }
        .rich-report-renderer .phase5-internal-subsection-button:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
        }
        .rich-report-renderer .phase5-internal-subsection-chevron {
          width: 1rem;
          height: 1rem;
          flex: 0 0 auto;
          transform: rotate(-90deg);
          transition: transform 120ms ease;
        }
        .rich-report-renderer .phase5-internal-subsection-chevron.is-expanded {
          transform: rotate(0deg);
        }
        .rich-report-renderer .phase5-internal-subsection-content {
          min-width: 0;
        }
      `}</style>
    </div>
  );
};

export default RichReportRenderer;
